'use client';

import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import Image from 'next/image';
import { PiMusicNotesSimpleThin } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { Sample } from '@/sharedTypes/Api';
import { rightLanguage } from '@/sharedTypes/helpers';
import samplesSelectorModel from '../Samples/SamplesSelector/SamplesSelectorModel';
import { useRouter, useSearchParams } from 'next/navigation';
import SamplesFinder from '../Samples/SamplesSelector/SamplesFinder';
import Pagination from '@/app/posts/components/Pagination';
import { SearchInput } from '@/components/ui/search-input';
import { TypographyH2, TypographyMuted } from '../ui/typography';
import { NEXT_PUBLIC_SAMPLES_CDN_URL } from '@/shared/envs';
import { Skeleton } from '../ui/skeleton';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';
import favoritesStore from '@/stores/favoritesStore';
import { formatComposerName, isArrangement } from '@/utils/composerUtils';

type SamplesGridProps = {
  category?: string;
  limit?: number;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  showFavoritesOnly?: boolean;
  selectedComposer?: string | null;
};

const SampleCard = observer(({ sample }: { sample: Sample }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const lang = useLanguageFromPath();
  const [imageLoaded, setImageLoaded] = useState(false);
  const title = rightLanguage(sample.metadata.title) || 'Untitled';
  const author = rightLanguage(sample.metadata.author) || 'Unknown';
  const previewImageUrl = sample.webpPath
    ? `${NEXT_PUBLIC_SAMPLES_CDN_URL || 'https://cdn.darpiano.com'}/${sample.webpPath}`
    : undefined;

  const handleClick = () => {
    // Preserve all existing URL params (including page number)
    const params = new URLSearchParams(searchParams.toString());
    params.set('sheet-music', sample.identifier);

    // Preserve the current language path
    const basePath = lang === 'ru' ? '/ru' : '/';
    router.push(`${basePath}?${params.toString()}`, { scroll: false });
  };

  return (
    <div onClick={handleClick} data-testid={`sample_grid_card_${sample.identifier}`} className='cursor-pointer group overflow-hidden animate-in fade-in duration-300'>
      {/* Preview Image */}
      <div className='aspect-[25/33] rounded-md border overflow-hidden w-full bg-white'>
        <div className='relative w-full h-full hover:zoom-in-50 transition-all duration-200'>
          {previewImageUrl ? (
            <>
              {!imageLoaded && (
                <Skeleton className='absolute inset-2' />
              )}
              <Image
                src={previewImageUrl}
                alt={`${title} music sheet preview`}
                fill
                className={`object-contain object-top p-2 transition-opacity duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                sizes='(max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw'
                onLoad={() => setImageLoaded(true)}
                unoptimized
              />
            </>
          ) : (
            <div className='flex items-center justify-center w-full h-full text-zinc-400'>
              {/* <PiMusicNotesSimpleFill size={32} /> */}
              <PiMusicNotesSimpleThin size={32} />
            </div>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className='pt-2'>
        <h3 className='text-base text-accent-blue-600 font-medium capitalize'>
          {title}
        </h3>
        <p className='text-sm text-zinc-500 capitalize mt-0.5'>{author}</p>
      </div>
    </div>
  );
});

const SamplesGrid = observer(
  ({ category, limit, searchQuery, onSearchChange, showFavoritesOnly = false, selectedComposer }: SamplesGridProps) => {
    const { t } = useTranslation();
    const router = useRouter();
    const searchParams = useSearchParams();
    const lang = useLanguageFromPath();
    const [isLoading, setIsLoading] = useState(true);
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { favoriteItems, loadInitialFavorites } = favoritesStore;

    // Read initial page from URL params, default to 1
    const pageFromUrl = searchParams.get('page');
    const initialPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
    const [currentPage, setCurrentPage] = useState(initialPage);
    const ITEMS_PER_PAGE = 30;

    useEffect(() => {
      const loadSamples = async () => {
        if (samplesSelectorModel.samples.length === 0) {
          await samplesSelectorModel.refreshSamples();
        }
        setIsLoading(false);
      };

      loadSamples();
      loadInitialFavorites(); // store method — stable ref, [] is correct
    }, []);

    // Prevent auto-focus on mobile devices
    useEffect(() => {
      const isMobileDevice =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isMobile = isMobileDevice || isTouchDevice;

      if (isMobile && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.blur();
        }, 0);
      }
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchQuery(value);
      onSearchChange?.(value);
    };

    // Sync state with URL params when they change
    useEffect(() => {
      const pageFromUrl = searchParams.get('page');
      const urlPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
      if (urlPage !== currentPage) {
        setCurrentPage(urlPage);
      }
    }, [searchParams, currentPage]);

    // Reset to page 1 when search, category, favorites, or composer filter changes (but not when page param changes)
    const prevSearchQuery = React.useRef(searchQuery);
    const prevCategory = React.useRef(category);
    const prevShowFavoritesOnly = React.useRef(showFavoritesOnly);
    const prevSelectedComposer = React.useRef(selectedComposer);

    useEffect(() => {
      const searchChanged = prevSearchQuery.current !== searchQuery;
      const categoryChanged = prevCategory.current !== category;
      const favoritesChanged = prevShowFavoritesOnly.current !== showFavoritesOnly;
      const composerChanged = prevSelectedComposer.current !== selectedComposer;

      if (!limit && (searchChanged || categoryChanged || favoritesChanged || composerChanged)) {
        prevSearchQuery.current = searchQuery;
        prevCategory.current = category;
        prevShowFavoritesOnly.current = showFavoritesOnly;
        prevSelectedComposer.current = selectedComposer;

        setCurrentPage(1);
        // Update URL to page 1
        const params = new URLSearchParams(searchParams.toString());
        params.delete('page'); // Remove page param when on page 1
        const basePath = lang === 'ru' ? '/ru' : '';
        router.push(`${basePath}?${params.toString()}`, { scroll: false });
      }
    }, [searchQuery, category, showFavoritesOnly, selectedComposer, limit, searchParams, router, lang]);

    // Handle edge case where current page exceeds total pages after filtering
    useEffect(() => {
      if (!limit && samplesSelectorModel.samples.length > 0) {
        // Filtering order: 1. Composer, 2. Search, 3. Favorites, 4. Category
        let filteredSamples = samplesSelectorModel.samples;

        // 1. FIRST: Filter by composer (if selected)
        if (selectedComposer) {
          filteredSamples = filteredSamples.filter((s) => {
            let fullName = rightLanguage(s.metadata?.author);
            if (!fullName) return false;

            // If author is an arrangement, use category name instead
            if (isArrangement(fullName)) {
              fullName = rightLanguage(s.category);
              if (!fullName) return false;
            }

            const formattedName = formatComposerName(fullName, lang as 'en' | 'ru');
            return formattedName === selectedComposer;
          });
        }

        // 2. THEN: Apply search query
        if (searchQuery && searchQuery.length > 0) {
          const samplesFinder = new SamplesFinder(filteredSamples);
          filteredSamples = samplesFinder.search(searchQuery);
        }

        // 3. THEN: Apply favorites filter
        if (showFavoritesOnly) {
          const favoriteIds = favoriteItems.map(fav => fav.id);
          filteredSamples = filteredSamples.filter(s => favoriteIds.includes(s.identifier));
        }

        // 4. FINALLY: Apply category filter
        if (category) {
          filteredSamples = filteredSamples.filter(
            (s) =>
              rightLanguage(s.category)?.toLowerCase() ===
              category.toLowerCase()
          );
        }

        const totalPagesCalc = Math.ceil(
          filteredSamples.length / ITEMS_PER_PAGE
        );
        if (currentPage > totalPagesCalc && totalPagesCalc > 0) {
          setCurrentPage(totalPagesCalc);
          const params = new URLSearchParams(searchParams.toString());
          if (totalPagesCalc === 1) {
            params.delete('page');
          } else {
            params.set('page', totalPagesCalc.toString());
          }
          const basePath = lang === 'ru' ? '/ru' : '';
          router.push(`${basePath}?${params.toString()}`, { scroll: false });
        }
      }
    }, [
      samplesSelectorModel.samples.length,
      currentPage,
      limit,
      searchQuery,
      category,
      showFavoritesOnly,
      selectedComposer,
      favoriteItems,
      searchParams,
      router,
      lang,
    ]);

    // Filtering order: 1. Composer, 2. Search, 3. Favorites, 4. Category
    let samples = samplesSelectorModel.samples;

    // 1. FIRST: Filter by composer (if selected)
    if (selectedComposer) {
      samples = samples.filter((s) => {
        let fullName = rightLanguage(s.metadata?.author);
        if (!fullName) return false;

        // If author is an arrangement, use category name instead
        if (isArrangement(fullName)) {
          fullName = rightLanguage(s.category);
          if (!fullName) return false;
        }

        const formattedName = formatComposerName(fullName, lang as 'en' | 'ru');
        return formattedName === selectedComposer;
      });
    }

    // 2. THEN: Apply search query
    if (searchQuery && searchQuery.length > 0) {
      const samplesFinder = new SamplesFinder(samples);
      samples = samplesFinder.search(searchQuery);
    }

    // 3. THEN: Apply favorites filter
    if (showFavoritesOnly) {
      const favoriteIds = favoriteItems.map(fav => fav.id);
      samples = samples.filter(s => favoriteIds.includes(s.identifier));
    }

    // 4. FINALLY: Apply category filter
    if (category) {
      samples = samples.filter(
        (s) =>
          rightLanguage(s.category)?.toLowerCase() === category.toLowerCase()
      );
    }

    // Calculate pagination values
    const totalPages = Math.ceil(samples.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    // Slice samples for current page (or apply limit if specified)
    const displayedSamples = limit
      ? samples.slice(0, limit)
      : samples.slice(startIndex, endIndex);

    // Handler for page changes with scroll-to-top and URL update
    const handlePageChange = (page: number) => {
      setCurrentPage(page);

      // Update URL with new page number
      const params = new URLSearchParams(searchParams.toString());
      if (page === 1) {
        params.delete('page'); // Clean URL for page 1
      } else {
        params.set('page', page.toString());
      }
      const basePath = lang === 'ru' ? '/ru' : '';
      router.push(`${basePath}?${params.toString()}`, { scroll: false });

      // Smooth scroll to top of page
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    };

    if (isLoading) {
      return (
        <>
          <div>
            <TypographyH2>{t('mainPage.samplesGrid.title')}</TypographyH2>
            <TypographyMuted>
              {t('mainPage.samplesGrid.subtitle')}
            </TypographyMuted>
          </div>

          <div className='pb-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8'>
            {[...Array(10)].map((_, i) => (
              <div key={i} className='space-y-2'>
                <Skeleton className='aspect-[25/33] rounded-md' />
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-3/4' />
                  <Skeleton className='h-3 w-1/2' />
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (samples.length === 0) {
      return (
        <div className='text-center py-12 text-zinc-500'>
          {t('mainPage.samplesGrid.noResults')}
        </div>
      );
    }

    return (
      <div className='flex flex-col gap-6'>
        <div>
          <TypographyH2>{t('mainPage.samplesGrid.title')}</TypographyH2>
          <TypographyMuted>
            {t('mainPage.samplesGrid.subtitle')}
          </TypographyMuted>
        </div>
        {/* Mobile Search & Filters - only visible on mobile/tablet */}
        <div className='lg:hidden mb-6 flex flex-col gap-3'>
          <SearchInput
            ref={inputRef}
            value={localSearchQuery}
            onChange={handleSearchChange}
            placeholder={t('mainPage.samplesGrid.searchPlaceholder')}
            testId='mobile_search_input'
          />
          {/* <MobileFilters /> */}
        </div>

        {/* Samples grid */}
        <div
          data-testid="samples_grid_container"
          className={`pb-8 grid ${
          displayedSamples.length <= 4
            ? 'grid-cols-[repeat(auto-fill,minmax(160px,180px))] gap-4'
            : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8'
        }`}>
          {displayedSamples.map((sample) => (
            <div key={sample.identifier} className={displayedSamples.length > 4 ? 'w-full' : ''}>
              <SampleCard sample={sample} />
            </div>
          ))}
        </div>

        {/* Pagination component */}
        {!limit && displayedSamples.length > 0 && totalPages > 1 && (
          <div data-testid="pagination_container">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalPosts={samples.length}
              hasNextPage={currentPage < totalPages}
              hasPreviousPage={currentPage > 1}
              onPageChange={handlePageChange}
              labels={{
                previous: t('mainPage.pagination.previous'),
                next: t('mainPage.pagination.next'),
                goToPage: t('mainPage.pagination.goToPage'),
                of: t('mainPage.pagination.of'),
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

export default SamplesGrid;
